import {
  isBefore,
  startOfHour,
  startOfDay,
  startOfWeek,
  startOfMonth,
  addMinutes,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  addYears,
} from '@e3dc-react/shell/Libs/date'
import { Resolution, ResolutionTypes } from '../types'
import { ChartView, ChartViews } from './chartModel'

/**
 * This function gets the available resolutions based on the view and custom range.
 */
export const getAvailableResolutions = (view: ChartView, isCustomRange?: boolean, customRangeInDays?: number): Resolution[] => {
  if (isCustomRange) {
    if (customRangeInDays && customRangeInDays <= 31 && customRangeInDays >= 2) {
      return [ResolutionTypes.FifteenMinutes, ResolutionTypes.Hour, ResolutionTypes.Day]
    } else if (customRangeInDays && customRangeInDays < 2) {
      return [ResolutionTypes.FifteenMinutes]
    }
    return [ResolutionTypes.FifteenMinutes, ResolutionTypes.Hour, ResolutionTypes.Day, ResolutionTypes.Month]
  }
  switch (view) {
    case ChartViews.Day:
      return [ResolutionTypes.FifteenMinutes]
    case ChartViews.Week:
      return [ResolutionTypes.FifteenMinutes, ResolutionTypes.Hour, ResolutionTypes.Day]
    case ChartViews.Month:
      return [ResolutionTypes.FifteenMinutes, ResolutionTypes.Hour, ResolutionTypes.Day]
    case ChartViews.Year:
      return [ResolutionTypes.FifteenMinutes, ResolutionTypes.Hour, ResolutionTypes.Day, ResolutionTypes.Month]
    default:
      return [ResolutionTypes.Day]
  }
}

/**
 * This function gets the label for a given resolution.
 */
export const getResolutionLabel = (resolution: Resolution, t: (key: string) => string): string => {
  const labels = {
    [ResolutionTypes.FifteenMinutes]: t('csv-export.resolution.15-minutes'),
    [ResolutionTypes.Hour]: t('csv-export.resolution.hours'),
    [ResolutionTypes.Day]: t('csv-export.resolution.days'),
    [ResolutionTypes.Week]: t('csv-export.resolution.weeks'),
    [ResolutionTypes.Month]: t('csv-export.resolution.months'),
    [ResolutionTypes.Year]: t('csv-export.resolution.years'),
  }
  return labels[resolution] || resolution
}

/**
 * This function gets the chart view for a given resolution to fill the time values.
 */
export const getChartViewForResolution = (resolution: Resolution): ChartView => {
  switch (resolution) {
    case ResolutionTypes.FifteenMinutes:
      return 'hour'
    case ResolutionTypes.Hour:
      return 'day'
    case ResolutionTypes.Day:
      return 'week'
    case ResolutionTypes.Week:
      return 'month'
    case ResolutionTypes.Month:
      return 'year'
    default:
      return 'day'
  }
}

/**
 * This function gets the history chart view used in fetching for a given resolution.
 */
export const getHistoryChartTimePeriodBasedOnResolution = (resolution: Resolution): ChartView => {
  switch (resolution) {
    case ResolutionTypes.FifteenMinutes:
      return 'day'
    case ResolutionTypes.Hour:
      return 'week'
    case ResolutionTypes.Day:
      return 'month'
    case ResolutionTypes.Week:
      return 'month'
    case ResolutionTypes.Month:
      return 'year'
    default:
      return 'day'
  }
}

/**
 * This function fills the time values based on the resolution.
 */
export const fillTimeValuesByResolution = ({ from, to, resolution }: { from: Date; to: Date; resolution: Resolution }): { ts: number }[] => {
  let currentTS = new Date(from)
  const timestampEntries: { ts: number }[] = []
  const { stepSize, stepUnit, shouldNormalizeStart } = (csvExportFns?.getStepSizeAndUnitByResolution ?? getStepSizeAndUnitByResolution)({
    resolution,
  })

  if (shouldNormalizeStart) {
    switch (stepUnit) {
      case 'minutes':
        // No normalization for minutes
        break
      case 'hour':
        currentTS = startOfHour(currentTS)
        break
      case 'day':
        currentTS = startOfDay(currentTS)
        break
      case 'week':
        currentTS = startOfWeek(currentTS)
        break
      case 'month':
        currentTS = startOfMonth(currentTS)
        break
    }
  }

  while (isBefore(currentTS, to)) {
    timestampEntries.push({ ts: currentTS.getTime() })

    switch (stepUnit) {
      case 'minutes':
        currentTS = addMinutes(currentTS, stepSize)
        break
      case 'hour':
        currentTS = addHours(currentTS, stepSize)
        break
      case 'day':
        currentTS = addDays(currentTS, stepSize)
        break
      case 'week':
        currentTS = addWeeks(currentTS, stepSize)
        break
      case 'month':
        currentTS = addMonths(currentTS, stepSize)
        break
    }
  }

  return timestampEntries
}

/**
 * Gets the step size and unit for a given resolution.
 */
export const getStepSizeAndUnitByResolution = ({
  resolution,
}: {
  resolution: Resolution
}): { stepSize: number; stepUnit: 'minutes' | 'hour' | 'day' | 'week' | 'month'; shouldNormalizeStart: boolean } => {
  switch (resolution) {
    case ResolutionTypes.Hour:
      return {
        stepSize: 1,
        stepUnit: 'hour',
        shouldNormalizeStart: true,
      }
    case ResolutionTypes.Day:
      return {
        stepSize: 1,
        stepUnit: 'day',
        shouldNormalizeStart: true,
      }
    case ResolutionTypes.Week:
      return {
        stepSize: 1,
        stepUnit: 'week',
        shouldNormalizeStart: true,
      }
    case ResolutionTypes.Month:
      return {
        stepSize: 1,
        stepUnit: 'month',
        shouldNormalizeStart: true,
      }
    default:
      return {
        stepSize: 15,
        stepUnit: 'minutes',
        shouldNormalizeStart: false,
      }
  }
}

const csvExportFns =
  typeof exports !== 'undefined'
    ? (exports as {
        getStepSizeAndUnitByResolution: typeof getStepSizeAndUnitByResolution
      })
    : undefined

/**
 * Increments the date by the appropriate amount based on resolution.
 */
export const incrementDateByResolution = (date: Date, resolution: Resolution): Date => {
  const incremented = new Date(date.getTime())

  if (resolution === ResolutionTypes.FifteenMinutes) {
    incremented.setMinutes(date.getMinutes() + 15)
  } else if (resolution === ResolutionTypes.Hour) {
    incremented.setHours(date.getHours() + 1)
  } else if (resolution === ResolutionTypes.Day) {
    incremented.setDate(date.getDate() + 1)
  } else if (resolution === ResolutionTypes.Week) {
    incremented.setDate(date.getDate() + 7)
  } else if (resolution === ResolutionTypes.Month) {
    // Native `setMonth` overflows into the following month when the current day doesn't
    // exist in the target month (e.g. Aug 31 -> Oct 1 instead of Sep 30). `addMonths`
    // clamps to the last day of the target month instead.
    return addMonths(date, 1)
  } else if (resolution === ResolutionTypes.Year) {
    // Same overflow issue as above, e.g. for Feb 29 in a non-leap target year.
    return addYears(date, 1)
  }
  return incremented
}
